import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ContainerSize } from '@freelancer/ui/container';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import type { SocialUrls } from '@freelancer/ui/social-buttons';
import { SocialButtonColor } from '@freelancer/ui/social-buttons';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { CargoCategoryDetailPages } from 'app/freightlancer/freightlancer-cargo-category-detail-pages/freightlancer-cargo-category-detail-pages.model';
import type { CargoCategory } from 'app/freightlancer/freightlancer.model';

@Component({
  selector: 'app-footer-freightlancer',
  template: `
    <div
      class="FooterSection"
      flTrackingSection="Footer"
    >
      <fl-container
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-grid [flMarginBottom]="'small'">
          <fl-col
            [colTablet]="12"
            [colDesktopSmall]="3"
            [flMarginRight]="'large'"
          >
            <div
              class="FooterInfo"
              [flMarginBottom]="'small'"
              [flMarginBottomTablet]="'large'"
            >
              <fl-link
                flTrackingLabel="flLogo"
                [link]="'/'"
              >
                <fl-logo
                  [size]="LogoSize.MID"
                  [backgroundColor]="BackgroundColor.DARK"
                >
                </fl-logo>
              </fl-link>
            </div>
          </fl-col>
          <fl-col
            class="FooterNav"
            [colTablet]="12"
            [colDesktopSmall]="5"
          >
            <fl-grid [flMarginBottom]="'xxxsmall'">
              <fl-col
                [colTablet]="6"
                [flMarginBottom]="'small'"
              >
                <fl-text
                  class="FooterTitle"
                  i18n="Company counter number"
                  [color]="FontColor.LIGHT"
                  [fontType]="FontType.CONTAINER"
                  [size]="TextSize.LARGE"
                  [weight]="FontWeight.BOLD"
                >
                  120+ million km
                </fl-text>
                <fl-text
                  i18n="Freight km posted counter label"
                  [color]="FontColor.LIGHT"
                  [size]="TextSize.SMALL"
                >
                  Freight posted per year
                </fl-text>
              </fl-col>
              <fl-col [colTablet]="6">
                <fl-text
                  class="FooterTitle"
                  i18n="Company counter number"
                  [color]="FontColor.LIGHT"
                  [fontType]="FontType.CONTAINER"
                  [size]="TextSize.LARGE"
                  [weight]="FontWeight.BOLD"
                >
                  80,000+
                </fl-text>
                <fl-text
                  i18n="Loads posted counter label"
                  [color]="FontColor.LIGHT"
                  [size]="TextSize.SMALL"
                >
                  Loads posted per year
                </fl-text>
              </fl-col>
            </fl-grid>
          </fl-col>
        </fl-grid>
        <fl-grid
          [flMarginBottomTablet]="'small'"
          [flMarginBottomDesktop]="'large'"
        >
          <fl-col
            [colTablet]="3"
            [colDesktopSmall]="2"
            [colOrder]="1"
            [colTabletOrder]="3"
            [flMarginRightDesktop]="'large'"
            [pull]="'right'"
          >
            <fl-text
              class="FooterTitle"
              i18n="Footer column title"
              [color]="FontColor.LIGHT"
              [flMarginBottom]="'xxxsmall'"
              [fontType]="FontType.SPAN"
              [size]="TextSize.SMALL"
              [weight]="FontWeight.BOLD"
            >
              Contact Us
            </fl-text>
            <div
              class="FooterNav"
              [flMarginBottom]="'small'"
            >
              <div
                class="ContactInfoRow"
                [flMarginBottom]="'xxxsmall'"
              >
                <fl-icon
                  [color]="IconColor.LIGHT"
                  [flMarginRight]="'xxxsmall'"
                  [name]="'ui-phone'"
                  [size]="IconSize.SMALL"
                ></fl-icon>
                <fl-link
                  flTrackingLabel="Footer phone link"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [link]="'tel:1300 562 374'"
                >
                  1300 562 374
                </fl-link>
              </div>
              <div
                class="ContactInfoRow"
                [flMarginBottom]="'xxxsmall'"
              >
                <fl-icon
                  [color]="IconColor.LIGHT"
                  [flMarginRight]="'xxxsmall'"
                  [name]="'ui-mail'"
                  [size]="IconSize.SMALL"
                ></fl-icon>
                <fl-link
                  flTrackingLabel="FooterEmailLink"
                  i18n="Footer email label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [link]="'mailto:info@loadshift.com.au'"
                >
                  info&#64;loadshift.com.au
                </fl-link>
              </div>
            </div>
          </fl-col>
          <fl-col
            [colTablet]="3"
            [colDesktopSmall]="3"
            [colOrder]="2"
            [colTabletOrder]="1"
            [flMarginRight]="'large'"
          >
            <fl-text
              class="FooterTitle"
              i18n="Footer column title"
              [color]="FontColor.LIGHT"
              [flMarginBottom]="'xxxsmall'"
              [fontType]="FontType.SPAN"
              [size]="TextSize.SMALL"
              [weight]="FontWeight.BOLD"
            >
              Getting Around
            </fl-text>
            <div
              class="FooterNav"
              [flMarginBottom]="'small'"
            >
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/"
                  i18n="Footer link label"
                  flTrackingLabel="goToHome"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Home
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/about"
                  i18n="Footer link label"
                  flTrackingLabel="goToAbout"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  About Us
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/how-it-works/shippers"
                  i18n="Footer shippers link label"
                  flTrackingLabel="goToShippersPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Shippers
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/how-it-works/carriers"
                  i18n="Footer shippers link label"
                  flTrackingLabel="goToShippersPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Carriers
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/credit-terms/butn"
                  i18n="Footer butn link label"
                  flTrackingLabel="goToButnPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Credit Terms
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/enterprise"
                  i18n="Enterprise link label"
                  flTrackingLabel="goToEnterprisePage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Enterprise
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/faqs"
                  i18n="Footer link label"
                  flTrackingLabel="goToFAQ"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  FAQ
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/transport-routes"
                  i18n="Footer transport routes link label"
                  flTrackingLabel="goToTransportRoutes"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Popular Freight Transport Routes
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/search/loads"
                  i18n="Footer link label"
                  flTrackingLabel="goToFindLoad"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Find a Load
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/login"
                  i18n="Footer link label"
                  flTrackingLabel="goToLogIn"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Log In
                </fl-link>
              </div>
              <div [flMarginBottom]="'xxxsmall'">
                <fl-link
                  link="/signup"
                  i18n="Footer link label"
                  flTrackingLabel="goToSignUp"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Sign Up
                </fl-link>
              </div>
            </div>
          </fl-col>
          <fl-col
            [colTablet]="5"
            [colDesktopSmall]="5"
            [colOrder]="3"
            [colTabletOrder]="2"
          >
            <fl-text
              class="FooterTitle"
              i18n="Footer categories column title"
              [color]="FontColor.LIGHT"
              [flMarginBottom]="'xxxsmall'"
              [fontType]="FontType.SPAN"
              [size]="TextSize.SMALL"
              [weight]="FontWeight.BOLD"
            >
              Cargo Categories
            </fl-text>
            <div
              class="FooterNav"
              [flMarginBottom]="'small'"
            >
              <div class="CargoCategories">
                <ng-container
                  *ngFor="let category of cargoCategories; trackBy: trackByLink"
                >
                  <div
                    class="CargoCategories-item"
                    [flMarginBottom]="'xsmall'"
                  >
                    <fl-link
                      flTrackingLabel="CargoCategoryLink"
                      i18n="Category Name"
                      [color]="LinkColor.MID"
                      [hoverColor]="LinkHoverColor.LIGHT"
                      [link]="'/cargo-categories/' + category.link"
                    >
                      {{ category.name }}
                    </fl-link>
                  </div>
                </ng-container>
              </div>
            </div>
          </fl-col>
          <fl-col
            [colTablet]="3"
            [colDesktopSmall]="2"
            [colOrder]="3"
            [colTabletOrder]="3"
            [flMarginRightDesktop]="'large'"
            [pull]="'right'"
          >
            <div class="PartnersAndTerms">
              <fl-text
                class="FooterTitle"
                i18n="Footer column title"
                [color]="FontColor.LIGHT"
                [flMarginBottom]="'xxxsmall'"
                [fontType]="FontType.SPAN"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Partners
              </fl-text>
              <div
                class="FooterNav"
                [flMarginBottom]="'small'"
              >
                <fl-link
                  class="FooterNav-item"
                  link="https://www.freelancer.com"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToFreelancer'"
                >
                  Freelancer
                </fl-link>
                <fl-link
                  class="FooterNav-item"
                  link="https://www.escrow.com"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToEscrow'"
                >
                  Escrow.com
                </fl-link>
              </div>
              <fl-text
                class="FooterTitle"
                i18n="Footer column title"
                [color]="FontColor.LIGHT"
                [flMarginBottom]="'xxxsmall'"
                [fontType]="FontType.SPAN"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Terms
              </fl-text>
              <div
                class="FooterNav"
                [flMarginBottom]="'small'"
              >
                <div [flMarginBottom]="'xxxsmall'">
                  <fl-link
                    link="/about/terms"
                    i18n="Footer link label"
                    flTrackingLabel="goToTerms"
                    [color]="LinkColor.MID"
                    [hoverColor]="LinkHoverColor.LIGHT"
                  >
                    Terms of Use
                  </fl-link>
                </div>
                <div [flMarginBottom]="'xxxsmall'">
                  <fl-link
                    flTrackingLabel="goToCodeOfConduct"
                    i18n="Code of Conduct link"
                    link="/info/codeofconduct"
                    [color]="LinkColor.MID"
                    [hoverColor]="LinkHoverColor.LIGHT"
                  >
                    Code of Conduct
                  </fl-link>
                </div>
                <div [flMarginBottom]="'xxxsmall'">
                  <fl-link
                    link="/about/privacy"
                    i18n="Footer link label"
                    flTrackingLabel="goToPrivacyPolicy"
                    [color]="LinkColor.MID"
                    [hoverColor]="LinkHoverColor.LIGHT"
                  >
                    Privacy Policy
                  </fl-link>
                </div>
              </div>
            </div>
          </fl-col>
        </fl-grid>

        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col
            [colTablet]="6"
            [colOrder]="2"
            [colTabletOrder]="1"
          >
            <fl-text [color]="FontColor.LIGHT">
              {{ 'Copyright &copy;' }} {{ currentTime | date: 'yyyy' }}
              {{ 'Loadshift Pty Ltd' }}
            </fl-text>
          </fl-col>
          <fl-col
            [colTablet]="2"
            [colDesktopSmall]="3"
            [colOrder]="1"
            [colTabletOrder]="2"
            [flMarginBottom]="'xsmall'"
            [pull]="'right'"
          >
            <div class="FreightlancerSocialIcons">
              <fl-social-buttons
                [color]="SocialButtonColor.LIGHT"
                [customSocialUrls]="socialUrls"
                [size]="IconSize.LARGE"
              ></fl-social-buttons>
            </div>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
  `,
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterFreightlancerComponent {
  BackgroundColor = BackgroundColor;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LogoSize = LogoSize;
  SocialButtonColor = SocialButtonColor;
  TextSize = TextSize;
  VerticalAlignment = VerticalAlignment;

  cargoCategories: readonly CargoCategory[] = [
    {
      name: 'Boat Transport',
      link: CargoCategoryDetailPages.BOAT_TRANSPORT,
    },
    {
      name: 'Bulk Tipper Transport',
      link: CargoCategoryDetailPages.BULK_TIPPER_TRANSPORT,
    },
    {
      name: 'Car Transport',
      link: CargoCategoryDetailPages.CAR_TRANSPORT,
    },
    {
      name: 'Container Transport',
      link: CargoCategoryDetailPages.CONTAINER_TRANSPORT,
    },
    {
      name: 'Furniture Removal',
      link: CargoCategoryDetailPages.FURNITURE_REMOVAL,
    },
    {
      name: 'General Freight',
      link: CargoCategoryDetailPages.GENERAL_FREIGHT,
    },
    {
      name: 'Grain Transport',
      link: CargoCategoryDetailPages.GRAIN_TRANSPORT,
    },
    {
      name: 'Hay Transport',
      link: CargoCategoryDetailPages.HAY_TRANSPORT,
    },
    {
      name: 'Heavy Haulage',
      link: CargoCategoryDetailPages.HEAVY_HAULAGE,
    },
    {
      name: 'Liquid Tanker Transport',
      link: CargoCategoryDetailPages.LIQUID_TANKER,
    },
    {
      name: 'Livestock Transport',
      link: CargoCategoryDetailPages.LIVESTOCK_TRANSPORT,
    },
    {
      name: 'Machinery Transport',
      link: CargoCategoryDetailPages.MACHINERY_TRANSPORT,
    },
    {
      name: 'Palletised Freight',
      link: CargoCategoryDetailPages.PALLETISED_FREIGHT,
    },
    {
      name: 'Portable Buildings',
      link: CargoCategoryDetailPages.PORTABLE_BUILDINGS,
    },
    {
      name: 'Trailer Transport',
      link: CargoCategoryDetailPages.TRAILER_TRANSPORT,
    },
    {
      name: 'Truck Transport',
      link: CargoCategoryDetailPages.TRUCK_TRANSPORT,
    },
  ];

  readonly currentTime: number = Date.now();
  size: ContainerSize;
  socialUrls: SocialUrls = {
    facebook: 'https://www.facebook.com/loadshift',
    instagram: 'https://www.instagram.com/loadshift',
    linkedin: 'https://www.linkedin.com/company/freightlancer/',
  };

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  trackByLink(_: number, item: CargoCategory): CargoCategoryDetailPages {
    return item.link;
  }
}
